import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { logError } from "@/lib/logger";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EditableText as T } from "@/components/live/EditableText";
import { Section } from "@/components/live/Section";
import { Helmet } from "react-helmet-async";

const FAQ = () => {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Placeholder FAQ items
  const placeholderFAQs = [
    {
      id: "1",
      question: "How do I arrange a meeting with you?",
      answer: "The best way to begin is by reviewing my Booking Etiquette page, then submitting an inquiry through my contact form. Please include as much detail as possible about yourself and what you're looking for. I personally review each inquiry and respond within 24-48 hours.",
      category: "booking"
    },
    {
      id: "2",
      question: "What is your screening process?",
      answer: "For everyone's safety, I require verification before meeting. This can be accomplished through provider references (preferred), professional verification via LinkedIn or company website, review site membership (Private Delights, TNA Board, P411), or alternative screening methods. All information is kept strictly confidential.",
      category: "booking"
    },
    {
      id: "4",
      question: "Do you travel?",
      answer: "Yes! I'm based in the NYC/NJ area but frequently visit California and am open to domestic travel with the right companion. Travel arrangements typically include airfare, lodging accommodations, and daily expenses.",
      category: "travel"
    },
    {
      id: "5",
      question: "What is your cancellation policy?",
      answer: "I understand that circumstances change. I ask for 24 hours notice for cancellations whenever possible. Last-minute cancellations without notice may affect future booking availability.",
      category: "booking"
    },
    {
      id: "6",
      question: "Are you available for overnight arrangements?",
      answer: "Yes, overnight arrangements are available for established connections and verified new acquaintances. These provide a more relaxed, intimate experience without the constraints of time.",
      category: "booking"
    },
    {
      id: "7",
      question: "What should I expect during our time together?",
      answer: "Expect genuine connection, engaging conversation, and an attentive companion who is fully present. I pride myself in my ability to adapt to various settings — whether it be a night out on the town, exploring your favorite art exhibits, or cheering on your favorite sports teams live from the bleachers. My goal is always to create a memorable, authentic experience.",
      category: "general"
    },
    {
      id: "10",
      question: "How far in advance should I book?",
      answer: "For the best availability, I recommend reaching out at least 48-72 hours in advance for local dates, and 1-2 weeks for travel. That said, same-day arrangements may be possible depending on my schedule.",
      category: "booking"
    }
  ];

  const fetchFAQs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/faq`);
      if (response.data && response.data.length > 0) {
        setFaqItems(response.data);
      } else {
        setFaqItems(placeholderFAQs);
      }
    } catch (error) {
      logError("Failed to fetch FAQs, using placeholders:", error);
      setFaqItems(placeholderFAQs);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFAQs();
  }, [fetchFAQs]);

  const categories = [
    { key: "all", label: "All Questions" },
    { key: "booking", label: "Booking" },
    { key: "travel", label: "Travel" },
    { key: "general", label: "General" },
  ];

  const [activeCategory, setActiveCategory] = useState("all");

  const filteredFAQs = activeCategory === "all"
    ? faqItems
    : faqItems.filter(item => item.category === activeCategory);

  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.question, acceptedAnswer: { "@type": "Answer", text: item.answer } })),
  };

  return (
    <div data-testid="faq-page">
      {faqItems.length > 0 && <Helmet><script type="application/ld+json">{JSON.stringify(faqSchema)}</script></Helmet>}
      {/* Header */}
      <section className="page-header" data-testid="faq-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="faq.common-questions" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Common Questions</T>
          <T k="faq.qa" as="h1" className="page-title animate-fade-in stagger-1">Q&A</T>
          <T k="faq.answers-to-frequently" as="p" className="page-subtitle animate-fade-in stagger-2">Answers to frequently asked questions about meeting me and what to expect.</T>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-6 md:px-12 lg:px-24" data-testid="faq-filter">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-5 py-2 rounded-full font-body text-sm tracking-wide transition-all duration-300 ${
                  activeCategory === cat.key
                    ? "bg-jade-deep text-white"
                    : "bg-sand text-stone-grey hover:bg-beige"
                }`}
                data-testid={`faq-filter-${cat.key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 px-6 md:px-12 lg:px-24" data-testid="faq-content">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFAQs.map((faq, index) => (
                <AccordionItem 
                  key={faq.id} 
                  value={faq.id}
                  className="glass rounded-xl px-6 border-none"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="font-heading text-lg text-charcoal hover:text-jade-text py-6 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-stone-grey leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <Section k="faq.section.faq-cta" label="faq cta" className="py-20 px-6 md:px-12 lg:px-24 bg-blush-pink/20" data-testid="faq-cta">
        <div className="max-w-3xl mx-auto text-center">
          <T k="faq.still-have-questions" as="h2" className="font-heading text-3xl text-charcoal mb-6">Still Have Questions?</T>
          <T k="faq.if-your-question" as="p" multiline className="font-body text-stone-grey leading-relaxed mb-8">If your question wasn't answered here, please don't hesitate to reach out. I'm happy to provide more information to help you feel comfortable.</T>
          <T k="faq.contact-me" as="a" href="/contact" className="btn-primary inline-block" data-testid="contact-link">Contact Me</T>
        </div>
      </Section>
    </div>
  );
};

export default FAQ;
